// Planificador local. Dijkstra sobre estados (parada, línea) más los nodos ficticios S (origen)
// y D (destino), que compiten con la opción "solo caminar". Sin dependencias del DOM ni del mapa
// para poder testearlo aisladamente y ejecutarlo también en Node.
//
// Estados:
//   "S"                  → origen ficticio (antes de tomar ningún bus)
//   "D"                  → destino ficticio
//   "<idx>|<line>"       → estar en la parada idx habiendo llegado por la línea line
//
// Transiciones:
//   S → D                :  ir andando directamente (compite siempre)
//   S → (i|l)            :  caminar del origen a una parada de la línea l  (+ 360 s de espera)
//   (i|l) → (j|l)        :  seguir a la parada siguiente de esa línea, en su sentido de
//                           circulación (m/4.2 s + 20 s dwell)
//   (i|l) → (j|l')       :  trasbordo andando < 180 m + 360 s espera
//   (i|l) → D            :  caminar de una parada al destino
//
// Puerto directo del prototipo, con dos mejoras: heap binario en lugar de sort() y separación de
// la reconstrucción de la ruta en función aparte.

import type { LatLng } from "./geo";
import { haversine } from "./geo";
import { MinHeap } from "./heap";
import { ROUTER_CONFIG, type RouterConfig } from "./config";
import type { BusStop, Dataset, EdgeMap, LineId } from "./data";

export interface Leg {
  type: "walk" | "wait" | "bus";
  time: number; // segundos
  // walk:
  from?: LatLng;
  to?: LatLng;
  meters?: number;
  // wait/bus:
  line?: LineId;
  // bus:
  stops?: number[]; // idx de paradas atravesadas
}

export interface RouteResult {
  totalSeconds: number;
  walkOnlySeconds: number;
  legs: Leg[];
  lines: LineId[]; // en orden de aparición
}

export interface RouteOptions {
  cfg?: Partial<RouterConfig>;
  banLines?: ReadonlySet<LineId>;
}

// Índices auxiliares. Se calculan una vez por Dataset y se cachean vía WeakMap para no rehacerlos
// en cada `route()`; a los tests les da igual porque la BD real cabe en 10 ms.
interface RouterIndex {
  adj: Map<LineId, Map<number, [number, number][]>>; // line → stopIdx → [otherIdx, meters]
  near: Map<number, [number, number][]>; // stopIdx → [otherIdx, meters] con m ≤ transferWalkMax
}
const INDEX_CACHE = new WeakMap<EdgeMap, RouterIndex>();

function buildIndex(ds: Dataset, cfg: RouterConfig): RouterIndex {
  const cached = INDEX_CACHE.get(ds.edges);
  if (cached) return cached;

  const adj = new Map<LineId, Map<number, [number, number][]>>();
  if (ds.routes.size > 0) {
    // Con los trayectos oficiales el grafo es dirigido: solo se puede ir de una parada a la
    // siguiente en el sentido en que circula el bus.
    for (const [lineId, routes] of ds.routes) {
      const m = new Map<number, [number, number][]>();
      for (const r of routes) {
        for (let k = 0; k + 1 < r.stops.length; k++) {
          const a = ds.stopsById.get(r.stops[k]!.id)?.idx;
          const b = ds.stopsById.get(r.stops[k + 1]!.id)?.idx;
          if (a === undefined || b === undefined || a === b) continue;
          const meters = Math.max(1, r.stops[k + 1]!.dist - r.stops[k]!.dist);
          const list = m.get(a) ?? [];
          const dup = list.find(([j]) => j === b);
          if (dup) dup[1] = Math.min(dup[1], meters);
          else list.push([b, meters]);
          m.set(a, list);
        }
      }
      adj.set(lineId, m);
    }
  } else {
    for (const [lineId, edges] of Object.entries(ds.edges)) {
      const m = new Map<number, [number, number][]>();
      for (const [a, b, meters] of edges) {
        if (!m.has(a)) m.set(a, []);
        if (!m.has(b)) m.set(b, []);
        m.get(a)!.push([b, meters]);
        m.get(b)!.push([a, meters]);
      }
      adj.set(lineId, m);
    }
  }

  // Trasbordos ≤ 180 m (haversine). N=267 → 267² comparaciones, ~70k pares, aceptable.
  const near = new Map<number, [number, number][]>();
  const N = ds.stops.length;
  for (let i = 0; i < N; i++) near.set(i, []);
  for (let i = 0; i < N; i++) {
    const si = ds.stopsByIdx[i]!;
    for (let j = i + 1; j < N; j++) {
      const sj = ds.stopsByIdx[j]!;
      const d = haversine(si.ll, sj.ll);
      if (d <= cfg.transferWalkMax) {
        near.get(i)!.push([j, d]);
        near.get(j)!.push([i, d]);
      }
    }
  }
  const idx: RouterIndex = { adj, near };
  INDEX_CACHE.set(ds.edges, idx);
  return idx;
}

function walkTime(meters: number, cfg: RouterConfig): number {
  return (meters * cfg.walkDetour) / cfg.walkSpeed;
}

function candidateStops(
  from: LatLng,
  stops: readonly BusStop[],
  cfg: RouterConfig,
): [number, number][] {
  // Devuelve [idx, metros] ordenadas por distancia. Si al menos `minCandidateStops` caen
  // dentro de `maxWalkToStop`, usa ese subconjunto; si no, coge las `fallbackCandidateStops`
  // más cercanas (para no dejar a nadie sin ruta en zonas mal cubiertas).
  const all: [number, number][] = stops.filter((s) => s.lines.length > 0).map((s) => [s.idx, haversine(from, s.ll)]);
  all.sort((a, b) => a[1] - b[1]);
  const within = all.filter(([, m]) => m <= cfg.maxWalkToStop);
  return within.length >= cfg.minCandidateStops ? within : all.slice(0, cfg.fallbackCandidateStops);
}

export function route(
  ds: Dataset,
  origin: LatLng,
  destination: LatLng,
  opts: RouteOptions = {},
): RouteResult | null {
  const cfg: RouterConfig = { ...ROUTER_CONFIG, ...opts.cfg };
  const ban = opts.banLines ?? new Set<LineId>();
  const { adj, near } = buildIndex(ds, cfg);

  const walkOnly = walkTime(haversine(origin, destination), cfg);

  const dist = new Map<string, number>();
  const prev = new Map<string, string | null>();
  const heap = new MinHeap<string>();

  const relax = (key: string, time: number, from: string | null): void => {
    const prevD = dist.get(key);
    if (prevD !== undefined && time >= prevD) return;
    dist.set(key, time);
    prev.set(key, from);
    heap.push(time, key);
  };

  relax("S", 0, null);

  const originCand = candidateStops(origin, ds.stops, cfg);
  const destCand = new Map(candidateStops(destination, ds.stops, cfg));

  while (heap.size > 0) {
    const { priority: t, value: k } = heap.pop()!;
    const knownT = dist.get(k);
    if (knownT === undefined || t > knownT) continue;
    if (k === "D") break;

    if (k === "S") {
      // Andar hasta el destino directo (compite siempre).
      relax("D", walkOnly, "S");
      // Andar hasta una parada candidata y esperar el bus.
      for (const [i, m] of originCand) {
        for (const l of ds.stopsByIdx[i]!.lines) {
          if (ban.has(l)) continue;
          relax(`${i}|${l}`, t + walkTime(m, cfg) + cfg.boardWaitSeconds, "S");
        }
      }
      continue;
    }

    const bar = k.indexOf("|");
    if (bar < 0) continue; // debería ser inalcanzable
    const i = Number(k.slice(0, bar));
    const l = k.slice(bar + 1);

    // 1) seguir por la misma línea
    for (const [j, m] of adj.get(l)?.get(i) ?? []) {
      relax(`${j}|${l}`, t + m / cfg.busSpeed + cfg.dwellSeconds, k);
    }
    // 2) trasbordo andando a otra línea en parada cercana
    for (const [j, m] of near.get(i) ?? []) {
      for (const l2 of ds.stopsByIdx[j]!.lines) {
        if (l2 === l || ban.has(l2)) continue;
        relax(`${j}|${l2}`, t + walkTime(m, cfg) + cfg.boardWaitSeconds, k);
      }
    }
    // 3) bajarse y andar al destino
    const destWalk = destCand.get(i);
    if (destWalk !== undefined) {
      relax("D", t + walkTime(destWalk, cfg), k);
    }
  }

  if (!dist.has("D")) return null;

  // Reconstruir la secuencia S → ... → D
  const seq: string[] = [];
  for (let k: string | null | undefined = "D"; k; k = prev.get(k)) seq.unshift(k);

  const pos = (k: string): LatLng => {
    if (k === "S") return origin;
    if (k === "D") return destination;
    const bar2 = k.indexOf("|");
    const idx = Number(k.slice(0, bar2));
    return ds.stopsByIdx[idx]!.ll;
  };

  const legs: Leg[] = [];
  let cur: Leg | null = null;
  for (let n = 1; n < seq.length; n++) {
    const a = seq[n - 1]!;
    const b = seq[n]!;
    const la = a.includes("|") ? a.slice(a.indexOf("|") + 1) : null;
    const lb = b.includes("|") ? b.slice(b.indexOf("|") + 1) : null;
    const dt = dist.get(b)! - dist.get(a)!;

    if (la !== null && la === lb) {
      const iA = Number(a.slice(0, a.indexOf("|")));
      const iB = Number(b.slice(0, b.indexOf("|")));
      if (cur && cur.type === "bus" && cur.line === la) {
        cur.stops!.push(iB);
        cur.time += dt;
      } else {
        cur = { type: "bus", line: la, stops: [iA, iB], time: dt };
        legs.push(cur);
      }
    } else {
      const m = haversine(pos(a), pos(b));
      const wait = lb !== null ? ROUTER_CONFIG.boardWaitSeconds : 0;
      // Solo pintamos "walk" si hay distancia real. `m > 5` filtra micro-transitos numéricos.
      if (m > 5 || lb === null || la === null) {
        legs.push({
          type: "walk",
          from: pos(a),
          to: pos(b),
          meters: Math.round(m * cfg.walkDetour),
          time: dt - wait,
        });
      }
      if (lb !== null) {
        legs.push({ type: "wait", line: lb, time: wait });
      }
      cur = null;
    }
  }

  // Descartar "wait" seguidos de nada (subimos y no viajamos en esa línea).
  const cleaned: Leg[] = [];
  for (let i = 0; i < legs.length; i++) {
    const g = legs[i]!;
    if (g.type === "wait") {
      const next = legs[i + 1];
      if (!next || next.type !== "bus" || next.line !== g.line) continue;
    }
    cleaned.push(g);
  }

  const lines: LineId[] = [];
  for (const g of cleaned) {
    if (g.type === "bus" && g.line !== undefined && !lines.includes(g.line)) lines.push(g.line);
  }

  return {
    totalSeconds: dist.get("D")!,
    walkOnlySeconds: walkOnly,
    legs: cleaned,
    lines,
  };
}
